import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  ExternalLink, 
  Search,
  FileText,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminMinisites = () => {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [minisites, setMinisites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchMinisites();
  }, []);

  const fetchMinisites = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/minisites/admin/list`);
      if (response.ok) {
        const data = await response.json();
        setMinisites(data);
      } else {
        console.error('Error response:', response.status);
        toast.error('Error carregant minisites');
      }
    } catch (error) {
      console.error('Error fetching minisites:', error);
      toast.error('Error carregant minisites');
    } finally {
      setLoading(false);
    }
  };

  const createMinisite = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/minisites/admin/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'Nou Minisite',
          status: 'draft'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Minisite creat');
        navigate(`/admin/minisites/${data.minisite.id}`);
      }
    } catch (error) {
      console.error('Error creating minisite:', error);
      toast.error('Error creant minisite');
    }
  };

  const duplicateMinisite = async (id) => {
    try {
      const response = await authFetch(`${API_URL}/api/minisites/admin/${id}/duplicate`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Minisite duplicat');
        fetchMinisites();
      }
    } catch (error) {
      console.error('Error duplicating minisite:', error);
      toast.error('Error duplicant minisite');
    }
  };

  const deleteMinisite = async (id) => {
    try {
      const response = await authFetch(`${API_URL}/api/minisites/admin/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Minisite eliminat');
        setDeleteConfirm(null);
        fetchMinisites();
      }
    } catch (error) {
      console.error('Error deleting minisite:', error);
      toast.error('Error eliminant minisite');
    }
  };

  const filteredMinisites = minisites.filter(m => 
    m.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-minisites">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E]">
              Minisites
            </h1>
            <p className="text-sm text-[#666666] mt-1">
              Crea i gestiona pàgines personalitzades
            </p>
          </div>
          <Button 
            onClick={createMinisite}
            className="bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nou Minisite
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
          <input
            type="text"
            placeholder="Cercar per títol o URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E]"
          />
        </div>

        {/* Minisites List */}
        {filteredMinisites.length === 0 ? (
          <div className="bg-white rounded-lg border border-[#E5E5E5] p-12 text-center">
            <FileText className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
            <p className="text-[#666666]">
              {searchTerm ? 'No s\'han trobat minisites' : 'Encara no hi ha minisites'}
            </p>
            {!searchTerm && (
              <Button 
                onClick={createMinisite}
                className="mt-4 bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear el primer minisite
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#E5E5E5] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F9F9F7] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Títol
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    URL
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Estat
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Actualitzat
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Accions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {filteredMinisites.map((minisite) => (
                  <tr key={minisite.id} className="hover:bg-[#F9F9F7]">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {minisite.header_image ? (
                          <img 
                            src={minisite.header_image} 
                            alt="" 
                            className="w-12 h-8 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-8 bg-[#E5E5E5] rounded flex items-center justify-center">
                            <FileText className="w-4 h-4 text-[#999999]" />
                          </div>
                        )}
                        <span className="font-medium text-[#333333]">
                          {minisite.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-sm text-[#666666] bg-[#F5F5F5] px-2 py-1 rounded">
                        /m/{minisite.slug}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        minisite.status === 'published' 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {minisite.status === 'published' ? (
                          <><Eye className="w-3 h-3" /> Publicat</>
                        ) : (
                          <><EyeOff className="w-3 h-3" /> Esborrany</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#666666]">
                      {new Date(minisite.updated_at).toLocaleDateString('ca-ES', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {minisite.status === 'published' && (
                          <a
                            href={`/m/${minisite.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-[#666666] hover:text-[#1A4D2E] hover:bg-[#F5F5F5] rounded"
                            title="Veure"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => navigate(`/admin/minisites/${minisite.id}`)}
                          className="p-2 text-[#666666] hover:text-[#1A4D2E] hover:bg-[#F5F5F5] rounded"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => duplicateMinisite(minisite.id)}
                          className="p-2 text-[#666666] hover:text-[#1A4D2E] hover:bg-[#F5F5F5] rounded"
                          title="Duplicar"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(minisite.id)}
                          className="p-2 text-[#666666] hover:text-red-600 hover:bg-red-50 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-[#333333] mb-2">
                Eliminar minisite?
              </h3>
              <p className="text-[#666666] mb-6">
                Aquesta acció no es pot desfer. El minisite i tot el seu contingut s'eliminaran permanentment.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel·lar
                </Button>
                <Button
                  onClick={() => deleteMinisite(deleteConfirm)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMinisites;
